// @ts-strict-ignore
import { FormInstance, useForm, useWatch } from 'antd/lib/form/Form';
import Modal from 'antd/lib/modal/Modal';
import Spin from 'antd/lib/spin';
import { ErrorFallback } from './ErrorBoundary';
import { flatMap } from 'lodash';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { UpdateIntegrationRequestDataType } from '../actions/integrations';
import {
    selectAppSettings,
    selectAppStoreType,
    selectOrgProviders,
    selectAppName,
    selectWorkflowEnabledSteps,
} from '../selectors/selectors';
import { FormFieldData, integrationModalContentUsingFormInstance } from '../helpers/formHelpers';
import { updateIntegrationFormDataForIntegration } from '../helpers/integrationFormHelpers';
import { validateUpdateIntegrationValues } from '../helpers/integrationHelpers';
import { RootState } from '../reducers/reducers';
import {
    AppPlatform,
    AppStoreType,
    Integration,
    IntegrationAzureIT,
    IntegrationFirebaseBeta,
    IntegrationInstallationId,
    IntegrationProvider,
    IntegrationType,
    SettingField,
    WorkflowEnabledSteps,
} from '../types';
import { AppComponentProps, AppRouteComponentMatchParams } from '../typescriptHelpers';
import { additionalInfoNodeForProvider } from './IntegrationConfiguration';
import { globalMessage } from '../utils/globalMessage';
import { useEffect } from 'react';
import { useIntegrationSettingFields } from '../utils/hooks/useIntegrationSettingFields';

interface EditIntegrationModalModalProps extends AppComponentProps {
    integration: Integration;
    open: boolean;
    isLoading: boolean;
    updateIntegration: (
        integrationInstallationId: IntegrationInstallationId,
        integrationType: IntegrationType,
        data: UpdateIntegrationRequestDataType,
    ) => void;
    onCancel: (integrationId: IntegrationInstallationId) => void;
}

const EditIntegrationModal: React.FC<EditIntegrationModalModalProps> = (props: EditIntegrationModalModalProps) => {
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const [formIsValid, setFormIsValid] = React.useState(false);
    const [form] = useForm();
    const { settingFields, isLoadingSettingFields, isSettingFieldsError } = useIntegrationSettingFields(
        props.orgId,
        props.appId,
        props.integration.installationId,
    );

    const [selectedAzureProjectId, setSelectedAzureProjectId] = React.useState(
        (props.integration as IntegrationAzureIT)?.data.selectedProject?.id ?? null,
    );
    const [selectedFirebaseProjectId, setSelectedFirebaseProjectId] = React.useState(
        (props.integration as IntegrationFirebaseBeta)?.data.projectId ?? null,
    );
    const integrationProvider = useSelector((state: RootState) => {
        const integrationProviders = selectOrgProviders(state, props.orgId);
        const provider = integrationProviders.find((provider) => provider.id === props.integration.providerId);

        return provider ?? null;
    });

    const appPlatform = useSelector((state: RootState) => selectAppSettings(state, props.appId).platform);
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const appName = useSelector((state: RootState) => selectAppName(state, props.appId));
    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, props.appId));

    const onFinish = (
        integrationInstallationId: IntegrationInstallationId,
        integrationId: IntegrationType,
        form: FormInstance,
    ) => {
        form.validateFields()
            .then((values) => {
                // BE-defined fields already use the save payload's shape
                const requestData =
                    settingFields ? values : (
                        validateUpdateIntegrationValues(integrationId, integrationProvider, values)
                    );

                if (requestData) {
                    props.updateIntegration(integrationInstallationId, integrationId, requestData);
                } else {
                    throw Error('Invalid form values');
                }
            })
            .catch((error) => {
                // Custom CI validates the trigger config only on submit; surface it as inline field
                // error text rather than a toast. onValuesChanged clears it once the user edits a field.
                if (props.integration.id === IntegrationType.CustomCI) {
                    form.setFields([{ name: 'triggerUrl', errors: [error.message] }]);
                } else {
                    globalMessage.error(error.message);
                }
                setFormIsValid(false);
            });
    };

    const onFieldsChange = (_: IntegrationType, form: FormInstance) => {
        const errors = flatMap(form.getFieldsError().map((field) => field.errors));

        const isValid = errors.length === 0;

        setFormIsValid(isValid);
    };

    // Fires only on actual value edits (not on setFields for errors), so it clears the submit-time
    // trigger-URL error as soon as the user starts fixing the form and re-enables the Update button.
    const onValuesChanged = (_: IntegrationInstallationId, __: IntegrationType, form: FormInstance) => {
        // Custom CI only: nothing else relies on onValuesChanged, so other integrations are untouched.
        if (props.integration.id !== IntegrationType.CustomCI) {
            return;
        }
        if (form.getFieldError('triggerUrl').length > 0) {
            form.setFields([{ name: 'triggerUrl', errors: [] }]);
        }
        const errors = flatMap(form.getFieldsError().map((field) => field.errors));
        setFormIsValid(errors.length === 0);
    };

    const firebaseProjectIdChanged = (value: string) => {
        setSelectedFirebaseProjectId(value);
        form.resetFields(['firebaseAppId', 'selectedBetaGroupIds']);
    };

    const azureProjectIdChanged = (value: string) => {
        form.resetFields(['projects']);
        setSelectedAzureProjectId(value);
    };

    const selectedAutoSelectAllJiraProjects = useWatch(['autoSelectAllProjects'], form);
    useEffect(() => {
        form.resetFields(['projects']);
    }, [selectedAutoSelectAllJiraProjects]);

    const fields = editDataForIntegration({
        integration: props.integration,
        settingFields,
        appPlatform,
        appStoreType,
        appId: props.appId,
        appName,
        workflowEnabledSteps,
        onChangeAzureProjectId: azureProjectIdChanged,
        onChangeFirebaseProjectId: firebaseProjectIdChanged,
        integrationProvider,
        azureProjectId: selectedAzureProjectId,
        firebaseProjectId: selectedFirebaseProjectId,
        autoSelectAllJiraProjects: selectedAutoSelectAllJiraProjects,
    });

    const modalContent = integrationModalContentUsingFormInstance(
        form,
        'vertical',
        props.integration.id,
        props.integration.installationId,
        fields,
        onFieldsChange,
        onFinish,
        onValuesChanged,
    );
    const additionalInfoForProvider =
        integrationProvider ?
            additionalInfoNodeForProvider(
                integrationProvider.type,
                props.integration.id,
                props.orgId,
                orgIdParam,
                appIdParam,
                props.integration.installationId,
            )
        :   null;

    return (
        <Modal
            title={`${props.integration.name} settings`}
            open={props.open}
            onCancel={() => props.onCancel(props.integration.installationId)}
            onOk={() => form.submit()}
            confirmLoading={props.isLoading}
            okButtonProps={{ disabled: !formIsValid }}
            okText="Update"
            width={props.integration.id === IntegrationType.CircleCI ? 700 : 520}
            destroyOnClose
            afterClose={() => {
                form.resetFields();
                setFormIsValid(false);
            }}
        >
            {isSettingFieldsError ?
                <ErrorFallback />
            :   <div>
                    {isLoadingSettingFields ?
                        <Spin />
                    :   modalContent}
                    {additionalInfoForProvider && <div style={{ marginTop: 12 }}>{additionalInfoForProvider}</div>}
                </div>
            }
        </Modal>
    );
};

export default EditIntegrationModal;

export const editDataForIntegration: (configData: {
    integration: Integration;
    settingFields: SettingField[] | undefined;
    appPlatform: AppPlatform;
    appStoreType: AppStoreType;
    appId: string;
    appName: string;
    workflowEnabledSteps: WorkflowEnabledSteps;
    onChangeAzureProjectId: (value) => void;
    onChangeFirebaseProjectId: (value) => void;
    integrationProvider?: IntegrationProvider;
    azureProjectId?: string;
    firebaseProjectId?: string;
    autoSelectAllJiraProjects?: boolean;
}) => FormFieldData[] = (configData) => {
    const {
        integration,
        settingFields,
        appPlatform,
        appStoreType,
        appId,
        appName,
        workflowEnabledSteps,
        onChangeAzureProjectId,
        onChangeFirebaseProjectId,
        integrationProvider,
        azureProjectId,
        firebaseProjectId,
        autoSelectAllJiraProjects,
    } = configData;

    return updateIntegrationFormDataForIntegration(
        integration,
        settingFields,
        appPlatform,
        appStoreType,
        appId,
        appName,
        workflowEnabledSteps,
        integrationProvider,
        onChangeAzureProjectId,
        azureProjectId,
        null,
        null,
        null,
        null,
        onChangeFirebaseProjectId,
        firebaseProjectId,
        null,
        null,
        autoSelectAllJiraProjects,
    );
};
